/**************************************************************************************************
 *
 *  This is the main container for all project related views: Project Detail (hierarch), Project
 *  Edit/Creation, Build Edit/Creation, Task Detail, Field Task Detail
 *
 *  This views are individual components that will fit into the detail-container parent element
 *  defined in this container.
 *
**************************************************************************************************/

import React from 'react';
import { connect } from 'react-redux';
import BaseComponent from '../../../baseClasses/BaseComponent';
import PopupMenu from '../../../components/PopupMenu';
import dataModel from '../../../models/DataModel';
import { getProjectProgress } from '../../../helpers/projectProgressUtil';
import './ProjectNavCard.scss';

// Bar width in rem-like units; 100% progress => 165% of base width for the filled segment
const PROGRESS_BAR_WIDTH_MULTIPLIER = 1.65;

class ProjectNavCard extends BaseComponent {

  constructor(props) {
    super(props);

    this.state = { menuVisible: false };
  }

  componentDidMount() {
    super.componentDidMount();
  }
  
  handleProjectSelection() {
    if (this.props.selectProject && !this.state.menuVisible && !this.ignoreSelection && !this.state.editingTitle) {
      this.props.selectProject(this.props.allProjects ? 'all' : this.props.project);
    }
    this.ignoreSelection = false;
  }

  /** MENU HANDLERS */
  handleMoreButton(e) {
    if (!this.state.menuVisible) {
      e.stopPropagation();
      this.setState({ menuVisible: true });
    }
    else {
      this.setState({ menuVisible: false });
    }
  }

  handleCloseMenu() {
    if (this.state.menuVisible) {
      if (!this.ignoreClose) {
        this.ignoreSelection = true;
      }
      this.ignoreClose = false;
      this.setState({ menuVisible: false });
    }
  }

  handleMenuSelection(type) {
    switch (type) {
    case 'delete':
      this.handleQueryDeleteProject();
      break;
    case 'archive':
      this.handleQueryArchiveProject();
      break;
    case 'unarchive':
      this.handleQueryUnarchiveProject();
      break;
    case 'edit':
      this.editProject();
      break;
    case 'copy':
      this.copyProject();
      break;
    default:
      break;
    }
    if (type) {
      this.ignoreSelection = false;
      this.ignoreClose = true;
    }
  }

  /** DELETE HANDLERS */
  handleDoDeleteProject() {
    const projectId = this.props.project.id;
    dataModel.projectsDAO.deleteProject(projectId).then(response => {
      dataModel.dataChanged = false;
      if (this.props.handleDeleteProject) {
        this.props.handleDeleteProject(projectId);
      }
    });
  }

  handleQueryDeleteProject() {
    const message = this.textMap.projectArchiveQuestion;

    dataModel.showGlobalModal('SYSTEM_POPUP', {
      message: message, 
      cancelButtonText: this.textMap.cancel, 
      okButtonText: this.textMap.deleteProject,
      height: 170,
      width: 320,
      handleOwnerOk: () => {
        this.handleDoDeleteProject();
      },
    });
  }

  /** ARCHIVE HANDLERS */
  handleArchiveProject() {
    const projectId = this.props.project.id;
    dataModel.projectsDAO.archiveProject(projectId).then(result => {
      // On success the project no longer matches the active (non-archived)
      // filter, so drop it from the sidebar list (same removal path delete
      // uses) to avoid showing a project that has been archived.
      if (result && this.props.handleDeleteProject) {
        this.props.handleDeleteProject(projectId);
      }
    }).catch(() => {
      // Error already surfaced to the user via displayHTTPError in the DAO;
      // keep the project in the list so they can retry.
    });
  }

  handleQueryArchiveProject() {
    const message = 'Are you sure you want to archive this project? Doing so will archive all builds and tasks associated with it. Archived projects may be viewed by selecting the archived filter.';

    dataModel.showGlobalModal('SYSTEM_POPUP', {
      message: message,
      cancelButtonText: this.textMap.cancel,
      okButtonText: 'Archive',
      height: 275,
      width: 300,
      handleOwnerOk: () => {
        this.handleArchiveProject();
      },
    });
  }

  /** UNARCHIVE HANDLERS */
  handleUnarchiveProject() {
    const projectId = this.props.project.id;
    dataModel.projectsDAO.unarchiveProject(projectId).then(result => {
      // On success the project no longer matches the active "Archived" filter, so
      // drop it from the sidebar list (same removal path delete uses) to avoid
      // showing a project that isn't archived anymore.
      if (result && this.props.handleDeleteProject) {
        this.props.handleDeleteProject(projectId);
      }
    }).catch(() => {
      // Error already surfaced to the user via displayHTTPError in the DAO;
      // keep the project in the list so they can retry.
    });
  }

  handleQueryUnarchiveProject() {
    const message = 'Are you sure you want to unarchive this project? Doing so will restore the project along with its builds and tasks.';

    dataModel.showGlobalModal('SYSTEM_POPUP', {
      message: message,
      cancelButtonText: this.textMap.cancel,
      okButtonText: 'Unarchive',
      height: 275,
      width: 300,
      handleOwnerOk: () => {
        this.handleUnarchiveProject();
      },
    });
  }

  /** EDIT/COPY HANDLERS */
  editProject() {
    if (this.props.editProject) {
      this.props.editProject(this.props.project);
    }
  }

  copyProject() {
    const { project } = this.props;
    dataModel.projectsDAO.copyProject(project).then((newProject) => {
      if (this.props.projectCopied) {
        this.props.projectCopied(newProject);
      }
    });
  }

  /** RENDER FUNCTIONS */
  createMoreMenu() {
    const { project } = this.props;
    const permissions = this.props.permissions || {};
    const { isSuperUser, canEditProject, canDeleteProject, canCopyProject } = permissions;
    const menuOptions = [];

    if (isSuperUser || (canEditProject && !project.archived_at)) {
      menuOptions.push({
        type: 'edit',
        name: this.textMap.editProject,
      });
    }
    if (isSuperUser || canDeleteProject) {
      menuOptions.push({
        type: 'delete',
        name: this.textMap.deleteProject,
      });
      if (!project.archived_at) {
        menuOptions.push({
          type: 'archive',
          name: 'Archive',
          separated: canCopyProject,
        });
      } else {
        menuOptions.push({
          type: 'unarchive',
          name: 'Unarchive',
          separated: canCopyProject,
        });
      }
    }
    if (isSuperUser || canCopyProject) {
      menuOptions.push({
        type: 'copy',
        name: this.textMap.copyProject,
      });
    }

    const menu = <PopupMenu left={245} top={10} width={200} menuOptions={menuOptions} size="smaller"
      menuId="projectMenu" popupOnHover={false} handleMenuSelection={this.handleMenuSelection}
      handleClickAway={this.handleCloseMenu} pointerPosition="left" />;
    return menu;
  }

  render() {

    const { selected, isFavorite, permissions, editEnabled } = this.props;
    let { project } = this.props;
    if (!project){
      project = this.props.project;
    }

    if (this.props.allProjects) {
      return (
        <div id="all-projects" className={'project-nav-card ' + (selected ? 'selected' : '')} onClick={this.handleProjectSelection}>
          <div className="info-line">
            <div className="project-info">
              <div className="project-title">
                All My Projects
            </div>
              <div className="project-status">
              </div>
            </div>
          </div>
        </div>
      );
    }
    else if (!project) {
      return null;
    }
    else {
      const fromStore = dataModel.projectsDAO.findItem(project.id);
      const projectForProgress = fromStore ? { ...fromStore, ...project } : project;
      const { progressPercent, completedCount, totalCount } = getProjectProgress(projectForProgress);
      const statusDisplay = completedCount + '/' + totalCount + ' | ';
      const percentDisplay = progressPercent + '%';
      const graphicWidth = PROGRESS_BAR_WIDTH_MULTIPLIER * progressPercent;

      const graphicStyle = {
        width: graphicWidth,
        background: progressPercent >= 100 ? '#5db348' : progressPercent > 0 ? '#f5a623' : '#9b9b9b',
      };

      let favoriteIndicator = null;
      if (isFavorite) {
        favoriteIndicator = <div className="favorite-indicator" onClick={() => this.props.handleRemoveFavorite(project.id)} />;
      }
      else {
        favoriteIndicator = <div className="not-favorite-indicator" onClick={() => this.props.handleMakeFavorite(project.id)} />;
      }

      let projectTitleView = <div className="project-title">
          {project.name}
        </div>;

      let projectStatusGraphic = null;
      projectStatusGraphic = <div className="project-status-graphic">
          <div className="background">
          </div>
          <div className="foreground" style={graphicStyle}>
          </div>
        </div>;

      let menuSelector = null;
      if (permissions && permissions.canEditProject && editEnabled) {
        menuSelector = <div className="menu-selector" onMouseDown={this.handleMoreButton} />;
      }
      let menu = null;
      if (this.state.menuVisible && permissions) {
        menu = this.createMoreMenu();
      }

      return (
        <div className={'project-nav-card ' + (selected ? 'selected' : '')} id={this.props.id} onClick={this.handleProjectSelection}>
          {menu}
          <div className="info-line">
            {favoriteIndicator}
            <div className="project-info">
              {projectTitleView}
              <div className="project-status">
                {statusDisplay}
                <span data-qa="project-status-percent">
                  {percentDisplay}
                </span>
              </div>
            </div>
          </div>
          {projectStatusGraphic}
          {menuSelector}
        </div>
      );
    }
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    permissions: state.new_users ? state.new_users.permissions : null,
    currentUser: state.new_users ? state.new_users.currentUser : null,
    currentCompany: state.new_companies ? state.new_companies.currentCompany : null,
    accentColor: state.new_companies.currentCompany ? state.new_companies.currentCompany.accent_color : null,
  };
};

export default connect(mapStateToProps)(ProjectNavCard);



// WEBPACK FOOTER //
// ./src/components/ProjectsNav/ProjectNavCard/index.js